'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { landingArtwork } from './landingArtwork'
import styles from './LandingRedesign.module.css'

const createFeatures = {
  images: {
    label: 'landingV2_featureImagesLabel',
    title: ['landingV2_featureImagesTitle1', 'landingV2_featureImagesTitle2'],
    description: 'landingV2_featureImagesBody',
    href: '/generate',
    action: 'landingV2_featureImagesAction',
  },
  tweak: {
    label: 'landingV2_featureTweakLabel',
    title: ['landingV2_featureTweakTitle1', 'landingV2_featureTweakTitle2'],
    description: 'landingV2_featureTweakBody',
    href: '/generate',
    action: 'landingV2_featureTweakAction',
  },
  video: {
    label: 'landingV2_featureVideoLabel',
    title: ['landingV2_featureVideoTitle1', 'landingV2_featureVideoTitle2'],
    description: 'landingV2_featureVideoBody',
    href: '/generate',
    action: 'landingV2_featureVideoAction',
  },
}

const chatFeatures = {
  roleplay: {
    label: 'landingV2_featureRoleplayLabel',
    title: [
      'landingV2_featureRoleplayTitle1',
      'landingV2_featureRoleplayTitle2',
    ],
    description: 'landingV2_featureRoleplayBody',
    href: '/chat',
    action: 'landingV2_featureRoleplayAction',
  },
  photos: {
    label: 'landingV2_featurePhotosLabel',
    title: ['landingV2_featurePhotosTitle1', 'landingV2_featurePhotosTitle2'],
    description: 'landingV2_featurePhotosBody',
    href: '/chat',
    action: 'landingV2_featurePhotosAction',
  },
  videos: {
    label: 'landingV2_featureVideosLabel',
    title: ['landingV2_featureVideosTitle1', 'landingV2_featureVideosTitle2'],
    description: 'landingV2_featureVideosBody',
    href: '/chat',
    action: 'landingV2_featureVideosAction',
  },
}

function FeatureVideo({ image, src }) {
  const player = useRef(null)
  const [requested, setRequested] = useState(false)
  const [inView, setInView] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const video = player.current
    if (!video) return
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updatePreference = () => {
      setReducedMotion(preference.matches)
      if (preference.matches) video.pause()
    }
    const pauseWhenHidden = () => {
      if (document.hidden) video.pause()
    }
    updatePreference()
    preference.addEventListener('change', updatePreference)
    document.addEventListener('visibilitychange', pauseWhenHidden)
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting)
        if (entry.isIntersecting) setRequested(true)
        else video.pause()
      },
      { threshold: 0.15 },
    )
    observer.observe(video)
    return () => {
      observer.disconnect()
      preference.removeEventListener('change', updatePreference)
      document.removeEventListener('visibilitychange', pauseWhenHidden)
      video.pause()
    }
  }, [failed])

  if (failed)
    return <Image {...image} fill sizes="(max-width: 700px) 63vw, 380px" />

  return (
    <video
      ref={player}
      className={styles.featureVideo}
      data-feature-video
      src={requested ? src : undefined}
      poster={image.src}
      aria-label={image.alt}
      muted
      playsInline
      controls
      preload="none"
      autoPlay={!reducedMotion && inView}
      onPlay={(event) => {
        if (!inView || document.hidden) event.currentTarget.pause()
      }}
      onError={() => setFailed(true)}
    />
  )
}

function ImagesScene({ video = false, active, t, artwork, videoSrc }) {
  const image = video ? artwork.rooftopBlonde : artwork.cafeCompanion
  return (
    <figure
      className={`${styles.imagesScene} ${video && videoSrc ? styles.videoScene : ''}`}
      aria-label={t(
        video ? 'landingV2_videoSceneAria' : 'landingV2_imageSceneAria',
      )}
    >
      <div className={styles.resultImage}>
        {video && active && videoSrc ? (
          <FeatureVideo image={image} src={videoSrc} />
        ) : (
          <Image {...image} fill sizes="(max-width: 700px) 63vw, 380px" />
        )}
        {video && (
          <span className={styles.sceneMediaLabel}>
            {t('landingV2_image')} <span aria-hidden="true">→</span>{' '}
            {t('landingV2_video')}
          </span>
        )}
      </div>
      <div className={styles.promptPreview}>
        <span className={styles.previewLabel}>
          {t(video ? 'landingV2_motionPrompt' : 'landingV2_prompt')}
        </span>
        <p>
          {t(
            video
              ? 'landingV2_motionPromptExample'
              : 'landingV2_imagePromptExample',
          )}
        </p>
      </div>
    </figure>
  )
}

function TweakScene({ t, artwork }) {
  const [reveal, setReveal] = useState(50)

  return (
    <figure
      className={styles.tweakScene}
      aria-label={t('landingV2_tweakSceneAria')}
    >
      <div className={styles.comparison}>
        <Image
          {...artwork.redheadAnime}
          fill
          sizes="(max-width: 700px) 85vw, 390px"
        />
        <div
          className={styles.comparisonAfter}
          style={{ clipPath: `inset(0 ${100 - reveal}% 0 0)` }}
        >
          <Image
            {...artwork.redheadTweak}
            fill
            sizes="(max-width: 700px) 85vw, 390px"
          />
        </div>
        <div className={styles.comparisonLabels} aria-hidden="true">
          <span>{t('landingV2_tweaked')}</span>
          <span>{t('landingV2_original')}</span>
        </div>
        <div
          className={styles.comparisonDivider}
          style={{ left: `${reveal}%` }}
          aria-hidden="true"
        >
          <span>‹ ›</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={reveal}
          onChange={(event) => setReveal(Number(event.target.value))}
          aria-label={t('landingV2_compareAria')}
          aria-valuetext={t('landingV2_compareValue', { percent: reveal })}
          aria-describedby="tweak-comparison-help"
        />
      </div>
      <figcaption id="tweak-comparison-help">
        “{t('landingV2_tweakPromptExample')}”
        <span>{t('landingV2_dragCompare')}</span>
      </figcaption>
    </figure>
  )
}

function ChatScene({ t, artwork }) {
  return (
    <figure
      className={styles.chatScene}
      aria-label={t('landingV2_roleplaySceneAria')}
    >
      <div className={styles.chatPortrait}>
        <Image
          {...artwork.cafeCompanion}
          fill
          sizes="(max-width: 700px) 70vw, 420px"
        />
      </div>
      <div className={styles.messages}>
        <p className={styles.received}>
          <em>{t('landingV2_chatMessage1a')}</em> {t('landingV2_chatMessage1b')}
        </p>
        <p className={styles.sent}>{t('landingV2_chatMessage2')}</p>
        <p className={styles.received}>{t('landingV2_chatMessage3')}</p>
      </div>
    </figure>
  )
}

function ChatMediaScene({ video = false, t, artwork }) {
  return (
    <figure
      className={styles.chatMediaScene}
      aria-label={t(
        video ? 'landingV2_chatVideoAria' : 'landingV2_chatPhotoAria',
      )}
    >
      <div className={styles.sceneConversation}>
        <p className={styles.sent}>
          {t(video ? 'landingV2_videoMessage1' : 'landingV2_photoMessage1')}
        </p>
        <p className={styles.received}>
          {t(video ? 'landingV2_videoMessage2' : 'landingV2_photoMessage2')}
        </p>
      </div>
      <div className={styles.mediaReply}>
        <span className={styles.mediaSender}>
          {t('landingV2_yourGirlfriend')}
        </span>
        <div className={styles.mediaAttachment}>
          <Image
            {...(video ? artwork.rooftopBlonde : artwork.cafeBlonde)}
            fill
            sizes="(max-width: 700px) 62vw, 300px"
          />
          <span className={styles.sceneMediaLabel}>
            {t(video ? 'landingV2_videoScene' : 'landingV2_photoFromHer')}
          </span>
        </div>
      </div>
    </figure>
  )
}

function FeatureScene({ id, active, t, artwork, videoSrc }) {
  if (id === 'tweak') return <TweakScene t={t} artwork={artwork} />
  if (id === 'roleplay') return <ChatScene t={t} artwork={artwork} />
  if (id === 'photos' || id === 'videos')
    return <ChatMediaScene video={id === 'videos'} t={t} artwork={artwork} />
  return (
    <ImagesScene
      video={id === 'video'}
      active={active}
      t={t}
      artwork={artwork}
      videoSrc={videoSrc}
    />
  )
}

export default function LandingFeatureShowcase({
  focus,
  artwork = landingArtwork,
  videoSrc,
}) {
  const { t } = useTranslation()
  const chatFirst = focus === 'chat'
  const features = chatFirst ? chatFeatures : createFeatures
  const order = Object.keys(features)
  const [selected, setSelected] = useState(order[0])
  const tabs = useRef({})

  function navigateTabs(event, id) {
    const index = order.indexOf(id)
    let next
    if (event.key === 'ArrowRight') next = order[(index + 1) % order.length]
    else if (event.key === 'ArrowLeft')
      next = order[(index - 1 + order.length) % order.length]
    else if (event.key === 'Home') next = order[0]
    else if (event.key === 'End') next = order[order.length - 1]
    else return
    event.preventDefault()
    setSelected(next)
    tabs.current[next]?.focus()
  }

  return (
    <section
      className={styles.showcase}
      aria-label={t(
        chatFirst
          ? 'landingV2_showcaseChatAria'
          : 'landingV2_showcaseCreateAria',
      )}
      data-landing-section="features"
    >
      <div className={styles.showcaseInner}>
        <div
          className={styles.featureTabs}
          role="tablist"
          aria-label={t('landingV2_tabsAria')}
        >
          {order.map((id) => (
            <button
              key={id}
              id={`feature-tab-${id}`}
              ref={(node) => {
                tabs.current[id] = node
              }}
              role="tab"
              type="button"
              aria-controls={`feature-panel-${id}`}
              aria-selected={selected === id}
              tabIndex={selected === id ? 0 : -1}
              onClick={() => setSelected(id)}
              onKeyDown={(event) => navigateTabs(event, id)}
              data-landing-action={`tab_${id === 'videos' ? 'video' : id}`}
            >
              {t(features[id].label)}
            </button>
          ))}
        </div>
        {order.map((id) => {
          const feature = features[id]
          return (
            <div
              key={id}
              id={`feature-panel-${id}`}
              role="tabpanel"
              aria-labelledby={`feature-tab-${id}`}
              hidden={selected !== id}
              tabIndex={0}
              className={styles.featurePanel}
            >
              <div className={styles.showcaseHeading}>
                <h2>
                  {t(feature.title[0])}
                  <br />
                  {t(feature.title[1])}
                </h2>
                <div className={styles.showcaseDescription}>
                  <p>{t(feature.description)}</p>
                  <Link
                    href={feature.href}
                    className={styles.textLink}
                    data-landing-cta={id}
                    data-landing-action={`feature_${id === 'videos' ? 'video' : id}`}
                  >
                    {t(feature.action)} <span aria-hidden="true">↗</span>
                  </Link>
                </div>
              </div>
              <div
                className={`${styles.stage} ${id === 'video' && videoSrc ? styles.videoStage : ''}`}
              >
                <FeatureScene
                  id={id}
                  active={selected === id}
                  t={t}
                  artwork={artwork}
                  videoSrc={videoSrc}
                />
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
